import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

const categoryLinks = [
  { nameKey: "cat.shoes", url: "https://kaspreadsheet.com/category-911-Shoes.html" },
  { nameKey: "cat.hoodies", url: "https://kaspreadsheet.com/search-912/name/Shoes.html" },
  { nameKey: "cat.tshirts", url: "https://kaspreadsheet.com/search-913/name/Shoes.html" },
  { nameKey: "cat.jackets", url: "https://kaspreadsheet.com/search-914/name/Shoes.html" },
  { nameKey: "cat.pants", url: "https://kaspreadsheet.com/search-915/name/Shoes.html" },
  { nameKey: "cat.headwear", url: "https://kaspreadsheet.com/search-916/name/Shoes.html" },
  { nameKey: "cat.sets", url: "https://kaspreadsheet.com/search-917/name/Shoes.html" },
  { nameKey: "cat.underwear", url: "https://kaspreadsheet.com/search-918/name/Shoes.html" },
  { nameKey: "cat.jersey", url: "https://kaspreadsheet.com/search-919/name/Shoes.html" },
  { nameKey: "cat.accessories", url: "https://kaspreadsheet.com/search-920/name/Shoes.html" },
  { nameKey: "cat.bags", url: "https://kaspreadsheet.com/search-921/name/Shoes.html" },
  { nameKey: "cat.belts", url: "https://kaspreadsheet.com/search-922/name/Shoes.html" },
  { nameKey: "cat.perfumes", url: "https://kaspreadsheet.com/search-923/name/Shoes.html" },
  { nameKey: "cat.other", url: "https://kaspreadsheet.com/search-924/name/Shoes.html" },
];

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setStatus("success");
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append("email", email);

      await fetch("https://readdy.ai/api/form/d8ulve39akq3s3asoam0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: formData.toString(),
      });

      setStatus("success");
      setEmail("");
    } catch {
      setStatus("error");
    }
  };

  return (
    <footer className="py-16 md:py-20 px-6 md:px-10 bg-background-50 border-t border-background-200/60">
      <div className="max-w-[1200px] mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left - Newsletter */}
          <div className="lg:w-[38%]">
            <h3 className="font-heading text-3xl md:text-4xl font-bold text-foreground-950 leading-tight mb-1">
              {t("footer.newsletter_title1")}
            </h3>
            <h3 className="font-heading text-3xl md:text-4xl italic font-light text-foreground-500 leading-tight mb-5">
              {t("footer.newsletter_title2")}
            </h3>
            <p className="text-sm text-foreground-500 leading-relaxed mb-8 max-w-[380px]">
              {t("footer.newsletter_desc")}
            </p>

            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="max-w-[380px]"
            >
              <label className="text-xs font-medium text-foreground-500 mb-2 block font-label">
                {t("footer.newsletter_label")}
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  name="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("footer.newsletter_placeholder")}
                  className="flex-1 bg-transparent border-0 border-b border-foreground-300 px-0 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-foreground-700 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === "success"}
                  className="px-6 py-2.5 rounded-full border border-foreground-300 text-sm font-medium text-foreground-700 hover:bg-foreground-50 hover:border-foreground-500 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                >
                  {status === "success" ? "✓" : t("footer.newsletter_button")}
                </button>
              </div>
              {/* Honeypot */}
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="visually-hidden-field"
              />
              {status === "success" && (
                <p className="text-xs text-accent-700 mt-3">Inscrição confirmada! Obrigado.</p>
              )}
              {status === "error" && (
                <p className="text-xs text-primary-600 mt-3">Erro ao inscrever. Tente novamente.</p>
              )}
            </form>
          </div>

          {/* Right - Links */}
          <div className="lg:w-[60%] grid grid-cols-2 sm:grid-cols-3 gap-8">
            {/* Quick Links */}
            <div>
              <h4 className="text-sm font-semibold text-foreground-900 mb-4 font-label">
                {t("footer.quick_links")}
              </h4>
              <ul className="space-y-2.5">
                <li>
                  <Link to="/" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors">
                    {t("nav.home")}
                  </Link>
                </li>
                <li>
                  <Link to="/guia-hoobuy-sheet" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors">
                    {t("nav.blog")}
                  </Link>
                </li>
                <li>
                  <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors">
                    Kaspreadsheet
                  </a>
                </li>
              </ul>
            </div>

            {/* Categories */}
            <div className="sm:col-span-2">
              <h4 className="text-sm font-semibold text-foreground-900 mb-4 font-label">
                {t("footer.categories_title")}
              </h4>
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                {categoryLinks.map((cat) => (
                  <a
                    key={cat.nameKey}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors whitespace-nowrap"
                  >
                    {t(cat.nameKey)}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-16 pt-8 border-t border-background-200/60 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-foreground-400">{t("footer.copyright")}</p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors">{t("footer.terms")}</a>
            <a href="#" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors">{t("footer.privacy")}</a>
            <a href="#" className="text-xs text-foreground-400 hover:text-foreground-600 transition-colors">{t("footer.contact")}</a>
          </div>
        </div>
      </div>
    </footer>
  );
}